import { Field, ImageField, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useMemo, useState } from 'react';
import ButtonDiscreet from 'components/shared/Button/ButtonDiscreet';
import ButtonSecondary from 'components/shared/Button/SecondaryButton';
import NextImage from 'next/image';
import { ComponentProps } from 'lib/component-props';
import { NearbySiteSummary } from 'components/SiteMaster/types';
import {
  getSiteDetailUrlFromCoveo,
  getFieldValue,
  useSiteMasterRouteFields,
} from 'components/SiteMaster/utils';
import useSitesResultList from 'lib/hooks/coveo/sitesHeadless/useSitesResultList';
import { SiteSearchResultRaw } from 'lib/types/coveo/SiteSearchResult';
import sitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/sitesHeadlessEngine';
import {
  StNearBySiteLocationButtonWrap,
  StNearBySiteLocationCard,
  StNearBySiteLocationCardHeader,
  StNearBySiteLocationGrid,
  StNearBySiteLocationLocationRow,
  StNearBySiteLocationSection,
  StNearBySiteLocationSiteName,
  StNearBySiteLocationTag,
  StNearBySiteLocationTagList,
  StNearBySiteLocationTitle,
} from './NearBySiteLocation.styles';
import { isTruthySitecoreParam, useNearBySiteSearchLocation } from './useNearBySiteSearchLocation';

type NearBySiteLocationProps = ComponentProps & {
  params?: {
    useProfileLocation?: string;
  };
  fields: {
    sectionTitle?: Field<string>;
    viewSiteButtonText?: Field<string>;
    siteFetchCount?: Field<number>;
    pinImage?: ImageField;
  };
};

const mapCoveoResultToNearbySite = (raw: SiteSearchResultRaw): NearbySiteSummary => ({
  id: raw.identifiableuri,
  name: raw.site_name,
  url: getSiteDetailUrlFromCoveo(raw),
  city: raw.site_city,
  state: raw.site_statecode || raw.site_state,
  distanceMiles: raw.distance ?? 0,
  conditions: raw.site_relatedconditions ?? [],
});

const DEFAULT_VISIBLE_CONDITIONS = 2;

const INLINE_DISCREET_BUTTON_STYLE = {
  minHeight: 'auto',
  height: 'auto',
  alignSelf: 'center',
  fontSize: '14px',
  fontWeight: 500,
  padding: '0 8px',
};

type NearBySiteLocationConditionTagsProps = {
  conditions: string[];
};

function NearBySiteLocationConditionTags({
  conditions,
}: NearBySiteLocationConditionTagsProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = Math.max(0, conditions.length - DEFAULT_VISIBLE_CONDITIONS);
  const visibleConditions = expanded ? conditions : conditions.slice(0, DEFAULT_VISIBLE_CONDITIONS);

  return (
    <StNearBySiteLocationTagList $expanded={expanded}>
      {visibleConditions.map((condition) => (
        <StNearBySiteLocationTag key={condition}>{condition}</StNearBySiteLocationTag>
      ))}
      {hiddenCount > 0 && (
        <ButtonDiscreet
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Show less' : `+${hiddenCount} more`}
          style={INLINE_DISCREET_BUTTON_STYLE}
          onClick={() => {
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? 'Show less' : `+${hiddenCount} more`}
        </ButtonDiscreet>
      )}
    </StNearBySiteLocationTagList>
  );
}

const formatLocationText = (site: NearbySiteSummary): string => {
  const location = [site.city, site.state].filter(Boolean).join(', ');
  const distance = `${site.distanceMiles.toFixed(1)} mi`;
  if (!location) {
    return distance;
  }
  return `${location} • ${distance}`;
};

function NearBySiteLocation(props: NearBySiteLocationProps): JSX.Element {
  const useProfileLocation = isTruthySitecoreParam(props.params?.useProfileLocation);
  const { latitude, longitude, isLocationReady } = useNearBySiteSearchLocation(useProfileLocation);
  const routeFields = useSiteMasterRouteFields();
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const siteName = getFieldValue(routeFields.siteName);
  const radiusMiles = Number(siteSettings?.siteRadius?.value ?? 50);
  const fetchCount = Number(
    props.fields.siteFetchCount?.value ?? siteSettings?.nearBySiteFetchCount?.value ?? 4
  );
  const pinImageSrc = props.fields.pinImage?.value?.src;

  const canSearch = isLocationReady && latitude != null && longitude != null;

  const { state } = useSitesResultList(sitesHeadlessEngine, {
    isSearchEnabled: canSearch,
    latitude,
    longitude,
    distanceMiles: radiusMiles,
    sortByDistance: true,
    resultsPerPage: fetchCount + 1,
  });

  const nearbySites = useMemo(
    () =>
      (state?.results ?? [])
        .map((result) => mapCoveoResultToNearbySite(result.raw as unknown as SiteSearchResultRaw))
        .filter((site) => site.name !== siteName)
        .slice(0, fetchCount),
    [state?.results, siteName, fetchCount]
  );

  if (!canSearch || nearbySites.length === 0) {
    return <></>;
  }

  return (
    <StNearBySiteLocationSection id="near-by-site-location-section">
      <StNearBySiteLocationTitle id="near-by-site-location-title">
        <Text field={props.fields.sectionTitle} />
      </StNearBySiteLocationTitle>

      <StNearBySiteLocationGrid id="near-by-site-location-grid">
        {nearbySites.map((site) => {
          const visibleConditions = site?.conditions ?? [];

          return (
            <StNearBySiteLocationCard key={site.id} id={`near-by-site-location-card-${site.id}`}>
              <StNearBySiteLocationCardHeader>
                <StNearBySiteLocationSiteName>{site.name}</StNearBySiteLocationSiteName>

                <StNearBySiteLocationLocationRow>
                  {pinImageSrc && (
                    <NextImage
                      src={props.fields.pinImage?.value?.src || ''}
                      width="16"
                      height="16"
                      alt={`${props.fields.pinImage?.value?.alt}`}
                      aria-hidden
                    />
                  )}
                  <span>{formatLocationText(site)}</span>
                </StNearBySiteLocationLocationRow>
              </StNearBySiteLocationCardHeader>

              {visibleConditions.length > 0 && (
                <NearBySiteLocationConditionTags conditions={visibleConditions} />
              )}

              <StNearBySiteLocationButtonWrap>
                <ButtonSecondary href={site.url} fullWidth>
                  <Text field={props.fields.viewSiteButtonText} />
                </ButtonSecondary>
              </StNearBySiteLocationButtonWrap>
            </StNearBySiteLocationCard>
          );
        })}
      </StNearBySiteLocationGrid>
    </StNearBySiteLocationSection>
  );
}

export default NearBySiteLocation;
